/**
 * Utility for managing route planning session state in sessionStorage
 * This allows users to navigate away from the planning page and return
 * without losing their current planning progress
 */

import type { PointItem, RouteData, ElevationPoint, Position } from '../types';
import type { RouteProfile } from '../components/profileSelector/RouteProfileSelector';
import { errorHandler } from '../services/errorHandler';
import { ErrorCodes, ErrorCategory, ErrorSeverity } from '../types/errors';

export interface PlanningSessionState {
  points: PointItem[];
  routeData: RouteData | null;
  elevationData: ElevationPoint[];
  routeProfile: RouteProfile;
  currentRouteName: string | null;
  currentRouteId: string | null;
  hasUnsavedChanges: boolean;
  // Map viewport state
  mapCenter: Position;
  mapZoom: number;
  // Route metrics
  routeMetrics: {
    distance: number; // in kilometers
    duration: number; // in minutes
    elevationGain: number; // in meters
  } | null;
  // History for undo/redo
  routeHistory: {
    points: PointItem[];
    routeData: RouteData | null;
    elevationData: ElevationPoint[];
  }[];
  redoHistory: {
    points: PointItem[];
    routeData: RouteData | null;
    elevationData: ElevationPoint[];
  }[];
  // Metadata
  lastSaved: string; // ISO timestamp
  sessionId: string; // Unique session identifier
}

const PLANNING_SESSION_KEY = 'cycling-buddy-planning-session';
const MAX_STORAGE_SIZE = 4 * 1024 * 1024; // 4MB safety limit (browsers usually allow 5-10MB)
const CLEANUP_THRESHOLD = 0.8; // Start cleanup when 80% of limit is reached

export class PlanningSessionManager {
  /**
   * Get the current storage usage in bytes
   */
  static getStorageSize(): number {
    let total = 0;
    for (let key in sessionStorage) {
      if (sessionStorage.hasOwnProperty(key)) {
        total += sessionStorage[key].length + key.length;
      }
    }
    return total;
  }

  /**
   * Check if storage is approaching quota limits
   */
  static isStorageNearLimit(): boolean {
    return this.getStorageSize() > (MAX_STORAGE_SIZE * CLEANUP_THRESHOLD);
  }

  /**
   * Clean up old or large session data
   */
  static cleanupStorage(): void {
    try {
      // Remove old cycling buddy data except current session
      const keysToRemove = [];
      for (let key in sessionStorage) {
        if (key.startsWith('cycling-buddy-') && key !== PLANNING_SESSION_KEY) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      // If still too large, compress current session by removing history
      if (this.isStorageNearLimit()) {
        const current = this.loadPlanningSession();
        if (current) {
          current.routeHistory = [];
          current.redoHistory = [];
          sessionStorage.setItem(PLANNING_SESSION_KEY, JSON.stringify(current));
        }
      }
    } catch (error) {
      console.warn('Storage cleanup failed:', error);
    }
  }
  /**
   * Save the current planning state to sessionStorage
   * This includes route points, map position, and zoom level
   */
  static savePlanningSession(
    routePoints: PointItem[],
    routeProfile: RouteProfile,
    mapCenter: [number, number],
    mapZoom: number,
    currentRouteName?: string | null,
    currentRouteId?: string | null,
    hasUnsavedChanges?: boolean,
    routeMetrics?: any,
    routeData?: RouteData | null,
    elevationData?: ElevationPoint[]
  ): void {
    // Check storage size before saving
    if (this.isStorageNearLimit()) {
      this.cleanupStorage();
    }

    const sessionState: PlanningSessionState = {
      points: routePoints,
      routeData: routeData || null,
      elevationData: elevationData || routeData?.elevationData || [],
      routeProfile,
      currentRouteName: currentRouteName || null,
      currentRouteId: currentRouteId || null,
      hasUnsavedChanges: hasUnsavedChanges || false,
      mapCenter: { lat: mapCenter[0], lng: mapCenter[1] },
      mapZoom,
      routeMetrics: routeMetrics || null,
      routeHistory: [],
      redoHistory: [],
      lastSaved: new Date().toISOString(),
      sessionId: crypto.randomUUID?.() || Date.now().toString()
    };
    
    const dataString = JSON.stringify(sessionState);
    
    try {
      // Check if this specific save would exceed limits
      if (dataString.length > MAX_STORAGE_SIZE * 0.9) {
        // Try to save a minimal version
        const minimalState = {
          ...sessionState,
          routeHistory: [],
          redoHistory: [],
          routeData: routeData ? {
            coordinates: routeData.coordinates,
            distance: routeData.distance,
            duration: routeData.duration
          } : null
        };
        sessionStorage.setItem(PLANNING_SESSION_KEY, JSON.stringify(minimalState));
      } else {
        sessionStorage.setItem(PLANNING_SESSION_KEY, dataString);
      }
      
    } catch (error) {
      // Enhanced error handling for storage issues
      if (error instanceof Error) {
        if (error.name === 'QuotaExceededError' || error.message.includes('quota')) {
          // Handle quota exceeded specifically
          const quotaError = errorHandler.createError(
            ErrorCategory.STORAGE,
            'Storage space is full. Route progress has been saved in reduced form.',
            {
              severity: ErrorSeverity.MEDIUM,
              code: ErrorCodes.STORAGE_QUOTA_EXCEEDED,
              technicalMessage: error.message,
              context: {
                feature: 'planning_session',
                operation: 'save',
                parameters: {
                  storageSize: this.getStorageSize(),
                  dataSize: dataString.length
                }
              },
              retryable: false
            }
          );
          errorHandler.handleError(quotaError);
          
          // Try emergency save with minimal data
          try {
            this.cleanupStorage();
            const emergencyState = {
              points: routePoints,
              routeProfile: routeProfile,
              mapCenter: { lat: mapCenter[0], lng: mapCenter[1] },
              mapZoom,
              lastSaved: new Date().toISOString()
            };
            sessionStorage.setItem(PLANNING_SESSION_KEY, JSON.stringify(emergencyState));
          } catch (emergencyError) {
            console.error('Emergency save also failed:', emergencyError);
          }
        } else {
          // Handle other storage errors
          const storageError = errorHandler.createError(
            ErrorCategory.STORAGE,
            'Failed to save route planning progress.',
            {
              severity: ErrorSeverity.LOW,
              code: ErrorCodes.STORAGE_SAVE_FAILED,
              technicalMessage: error.message,
              context: {
                feature: 'planning_session',
                operation: 'save'
              },
              retryable: true
            }
          );
          errorHandler.handleError(storageError);
        }
      }
      
      console.error('Failed to save planning session:', error);
    }
  }

  /**
   * Load the planning state from sessionStorage
   */
  static loadPlanningSession(): PlanningSessionState | null {
    try {
      const stored = sessionStorage.getItem(PLANNING_SESSION_KEY);
      if (!stored) {
        return null;
      }

      const state = JSON.parse(stored) as PlanningSessionState;
      
      // Backward compatibility: add routeMetrics if it doesn't exist
      if (!state.routeMetrics) {
        state.routeMetrics = null;
      } else {
        // Ensure route metrics are proper numbers (in case they were stored as strings)
        state.routeMetrics = {
          distance: typeof state.routeMetrics.distance === 'number' ? state.routeMetrics.distance : parseFloat(state.routeMetrics.distance as any) || 0,
          duration: typeof state.routeMetrics.duration === 'number' ? state.routeMetrics.duration : parseFloat(state.routeMetrics.duration as any) || 0,
          elevationGain: typeof state.routeMetrics.elevationGain === 'number' ? state.routeMetrics.elevationGain : parseFloat(state.routeMetrics.elevationGain as any) || 0
        };
      }
      
      // Validate that the state isn't too old (24 hours)
      const lastSaved = new Date(state.lastSaved);
      const now = new Date();
      const hoursDiff = (now.getTime() - lastSaved.getTime()) / (1000 * 60 * 60);
      
      if (hoursDiff > 24) {
        this.clearPlanningState();
        return null;
      }

      return state;
    } catch (error) {
      console.error('Failed to load planning state:', error);
      this.clearPlanningState();
      return null;
    }
  }

  /**
   * Clear the planning state from sessionStorage
   */
  static clearPlanningState(): void {
    try {
      sessionStorage.removeItem(PLANNING_SESSION_KEY);
    } catch (error) {
      console.error('Failed to clear planning state:', error);
    }
  }

  /**
   * Clear planning session when opening a saved route
   * This ensures we don't have conflicting state between saved routes and active planning
   */
  static clearPlanningSessionForSavedRoute(): void {
    this.clearPlanningState();
  }

  /**
   * Check if there's an active planning session
   */
  static hasPlanningSession(): boolean {
    return sessionStorage.getItem(PLANNING_SESSION_KEY) !== null;
  }

  /**
   * Update specific parts of the planning state without a full save
   */
  static updatePlanningState(updates: Partial<Omit<PlanningSessionState, 'lastSaved' | 'sessionId'>>): void {
    const currentState = this.loadPlanningSession();
    if (!currentState) {
      return;
    }

    const updatedState = {
      ...currentState,
      ...updates,
      lastSaved: new Date().toISOString()
    };

    try {
      sessionStorage.setItem(PLANNING_SESSION_KEY, JSON.stringify(updatedState));
    } catch (error) {
      console.error('Failed to update planning state:', error);
    }
  }

  /**
   * Get a summary of the current planning session for UI display
   */
  static getPlanningSessionSummary(): {
    hasSession: boolean;
    pointCount: number;
    routeName: string | null;
    hasRoute: boolean;
    mapCenter: Position | null;
    mapZoom: number | null;
    lastSaved: string | null;
  } {
    const state = this.loadPlanningSession();
    
    if (!state) {
      return {
        hasSession: false,
        pointCount: 0,
        routeName: null,
        hasRoute: false,
        mapCenter: null,
        mapZoom: null,
        lastSaved: null
      };
    }

    return {
      hasSession: true,
      pointCount: state.points.length,
      routeName: state.currentRouteName,
      hasRoute: state.routeData !== null,
      mapCenter: state.mapCenter,
      mapZoom: state.mapZoom,
      lastSaved: state.lastSaved
    };
  }

  static clearPlanningSession(): void {
    try {
      sessionStorage.removeItem(PLANNING_SESSION_KEY);
    } catch (error) {
      console.error('Failed to clear planning session:', error);
    }
  }
}
